import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Get API URL from environment
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:8040';

const PatientDetails = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const [patient, setPatient] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [patientMedia, setPatientMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId]);

  const fetchPatientDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Fetch patient information
      const patientResponse = await fetch(
        `${API_URL}/api/patients/${patientId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!patientResponse.ok) {
        throw new Error(`Failed to fetch patient: ${patientResponse.status}`);
      }

      const patientData = await patientResponse.json();
      setPatient(patientData);

      // Fetch appointments
      const appointmentsResponse = await fetch(
        `${API_URL}/api/patients/${patientId}/appointments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (appointmentsResponse.ok) {
        const appointmentsData = await appointmentsResponse.json();
        setPatientAppointments(appointmentsData.items || []);
      }

      // Fetch media
      const mediaResponse = await fetch(
        `${API_URL}/api/patients/${patientId}/media`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        setPatientMedia(mediaData.items || []);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching patient details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#075e54]"></div>
        <p className="ml-4 text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/patients')}
            className="flex items-center text-[#075e54] hover:text-[#064e45]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back', 'Back to Patients')}
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/patients')}
            className="flex items-center text-[#075e54] hover:text-[#064e45]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back', 'Back to Patients')}
          </button>
        </div>

        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Patient not found</h3>
          <p className="mt-1 text-sm text-gray-500">The patient you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/patients')}
            className="flex items-center text-[#075e54] hover:text-[#064e45]"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back', 'Back to Patients')}
          </button>
        </div>
      </div>

      {/* Patient Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('patient.details')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">{t('patient.name')}</p>
            <p className="text-lg text-gray-900">{patient.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('patient.phone')}</p>
            <p className="text-lg text-gray-900">{patient.phone_number}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('patient.languagePreference', 'Language Preference')}</p>
            <p className="text-lg text-gray-900">{patient.language_preference || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('patient.lastContact', 'Last Contact')}</p>
            <p className="text-lg text-gray-900">{formatDate(patient.updated_at)}</p>
          </div>
        </div>
        {patient.notes && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500">Notes</p>
            <p className="text-gray-900">{patient.notes}</p>
          </div>
        )}
        {patient.medical_history && patient.medical_history.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500">Medical History</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {patient.medical_history.map((item, index) => (
                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Appointments */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Appointments</h3>
        {patientAppointments.length === 0 ? (
          <p className="text-gray-500">No appointments found.</p>
        ) : (
          <div className="space-y-3">
            {patientAppointments.map((appointment) => (
              <div key={appointment.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {appointment.doctor_name || 'No doctor assigned'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {formatDate(appointment.appointment_date)} at {formatTime(appointment.appointment_start_time)}
                    </p>
                    {appointment.remarks && (
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{appointment.remarks}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {appointment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Media */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Media</h3>
        {patientMedia.length === 0 ? (
          <p className="text-gray-500">No media found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patientMedia.map((media) => (
              <div key={media.id} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {media.media_type === 'image' ? (
                      <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 4h16v16H4V4zm2 2v12h12V6H6zm2 10l2-3 2 2 4-5 2 2v4H8v-0z"/>
                      </svg>
                    ) : media.media_type === 'audio' ? (
                      <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM8 11a4 4 0 0 0 8 0h2a6 6 0 0 1-12 0h2zm4 6v3h-2v-3h2z"/>
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {media.filename || 'Untitled'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(media.created_at)}
                    </p>
                  </div>
                  {media.media_url && (
                    <a
                      href={media.media_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#075e54] hover:text-[#064e45] text-sm"
                    >
                      View
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientDetails;
